import { onMounted, onUnmounted, toValue, watch } from "vue"

const ACTIVE_POLL_INTERVAL = 60_000
const IDLE_POLL_INTERVAL = 300_000
const IDLE_AFTER = 120_000
const MAX_CONSECUTIVE_FAILURES = 3

/**
 * Polls the notifications store on an adaptive interval, slowing down while the
 * user is idle and pausing while the tab is hidden.
 *
 * @param {object} store - Notifications store exposing `loadNotifications`,
 *   `refreshNotifications`, and a writable `loadingFailed` flag.
 * @param {object} [options]
 * @param {boolean | import("vue").Ref<boolean> | (() => boolean)} [options.enabled]
 *   Whether polling should be running. Accepts a plain boolean, a ref, a
 *   computed, or a getter function — anything `toValue` can unwrap — so the caller
 *   can drive it from reactive state (e.g. `() => sessionStore.isAuthenticated`)
 *   and polling will start/stop as that value flips. When omitted, polling is always on.
 * @param {(error: unknown) => void} [options.onInitialLoadError] - Called if
 *   the first `loadNotifications` call rejects.
 */
export function useNotificationsPolling(
  store,
  { enabled, onInitialLoadError } = {},
) {
  function isEnabled() {
    return enabled == null || !!toValue(enabled)
  }
  let mounted = false
  let pollingTimer = null
  let lastActivity = Date.now()
  let consecutiveFailures = 0
  let initialLoadStarted = false

  function resetActivity() {
    lastActivity = Date.now()
  }

  function getPollingInterval() {
    return Date.now() - lastActivity > IDLE_AFTER
      ? IDLE_POLL_INTERVAL
      : ACTIVE_POLL_INTERVAL
  }

  function schedulePoll() {
    clearTimeout(pollingTimer)
    if (document.hidden || !isEnabled()) {
      return
    }

    pollingTimer = setTimeout(async () => {
      try {
        await store.refreshNotifications()
        consecutiveFailures = 0
      } catch {
        consecutiveFailures++
        if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
          store.loadingFailed = true
        }
      }
      if (mounted && isEnabled()) {
        schedulePoll()
      }
    }, getPollingInterval())
  }

  async function start() {
    if (initialLoadStarted) {
      schedulePoll()
      return
    }
    initialLoadStarted = true
    try {
      await store.loadNotifications()
    } catch (error) {
      consecutiveFailures++
      onInitialLoadError?.(error)
    }
    if (mounted && isEnabled()) {
      schedulePoll()
    }
  }

  function stop() {
    clearTimeout(pollingTimer)
    initialLoadStarted = false
    consecutiveFailures = 0
  }

  function handleVisibilityChange() {
    if (!document.hidden && isEnabled()) {
      schedulePoll()
    } else {
      clearTimeout(pollingTimer)
    }
  }

  onMounted(() => {
    mounted = true
    document.addEventListener("pointermove", resetActivity, { passive: true })
    document.addEventListener("keydown", resetActivity, { passive: true })
    document.addEventListener("visibilitychange", handleVisibilityChange)

    watch(
      isEnabled,
      (val) => {
        if (val) {
          start()
        } else {
          stop()
        }
      },
      { immediate: true },
    )
  })

  onUnmounted(() => {
    mounted = false
    clearTimeout(pollingTimer)
    document.removeEventListener("pointermove", resetActivity)
    document.removeEventListener("keydown", resetActivity)
    document.removeEventListener("visibilitychange", handleVisibilityChange)
  })
}
